import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const FAQItem = ({ faq, isOpen, onToggle, idx }) => {
  return (
    <motion.div
      initial={{ opacity: 0, x: 60 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: idx * 0.06, ease: [0.22, 1, 0.36, 1] }}
      className={`rounded-2xl border transition-all overflow-hidden ${
        isOpen
          ? "border-aurora-teal/40 bg-white/[0.04]"
          : "border-white/10 bg-white/[0.02] hover:border-white/20"
      }`}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-4 text-left p-5 md:p-6 focus:outline-none"
      >
        <span className="text-sm md:text-base font-semibold text-white/95">
          {faq.question}
        </span>
        <span
          className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center border transition-all ${
            isOpen
              ? "bg-gradient-to-br from-aurora-teal to-aurora-magenta border-transparent rotate-45"
              : "border-white/20 text-white/70"
          }`}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path
              d="M12 5v14M5 12h14"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </span>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="px-5 md:px-6 pb-5 md:pb-6 pt-0">
              <div className="h-px bg-gradient-to-r from-transparent via-aurora-teal/40 to-transparent mb-4" />
              <p className="text-sm text-white/65 leading-relaxed">
                {faq.answer}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const FAQs = ({ faqs }) => {
  const [openId, setOpenId] = useState(null);

  return (
    <section className="relative w-full px-6 md:px-12 lg:px-24 py-24 md:py-32 overflow-hidden">
      <div className="absolute top-0 right-1/4 w-[400px] h-[400px] bg-aurora-teal/10 blur-[140px] rounded-full pointer-events-none" />

      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr,1.4fr] gap-12 lg:gap-20">
        <motion.div
          initial={{ opacity: 0, x: -60 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        >
          <span className="eyebrow">
            <span className="dot" />
            FAQ
          </span>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-5 font-jamjuree text-3xl md:text-5xl font-semibold text-white leading-tight"
          >
            Frequently asked{" "}
            <span className="text-gradient">questions</span>.
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="mt-5 text-sm md:text-base text-white/65 leading-relaxed max-w-md"
          >
            Everything you might want to know before we start working
            together. Don't see your question? Just reach out.
          </motion.p>

          <div className="hidden lg:block mt-10 relative">
            <div className="absolute inset-0 bg-gradient-to-br from-aurora-teal/30 to-aurora-magenta/30 blur-3xl" />
            <div className="relative glass-strong rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-aurora-teal to-aurora-magenta flex items-center justify-center text-white font-semibold">
                  H
                </div>
                <div>
                  <div className="text-sm text-white font-medium">Hamza</div>
                  <div className="text-[11px] text-white/50">
                    Replies in &lt; 24h
                  </div>
                </div>
              </div>
              <p className="text-xs text-white/65 leading-relaxed">
                Got a different question? Drop me a message and I'll get back
                to you with answers.
              </p>
            </div>
          </div>
        </motion.div>

        <div className="flex flex-col gap-3">
          {faqs.map((faq, i) => (
            <FAQItem
              key={faq.id}
              faq={faq}
              idx={i}
              isOpen={openId === faq.id}
              onToggle={() => setOpenId(openId === faq.id ? null : faq.id)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQs;
